import React, {useEffect, useState} from "react";
import {
    Button,
    Checkbox,
    FormControl,
    FormControlLabel, FormGroup, FormLabel,
    Grid, InputAdornment,
    Link,
    Paper,
    Radio,
    RadioGroup,
    TableCell,
    TableRow,
    TextField
} from "@material-ui/core"
import _ from "lodash";
import {KeyboardDatePicker, MuiPickersUtilsProvider} from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import PublisherForm from "../../../publishers/PublisherForm";
import SingleAsyncAutoComplete from "../../../shared/SingleAsyncAutoComplete";
import useDataFetchers from "../../../../../hooks/useDataFetchers";
import {useField, useFieldWithValidation} from "../../../../../hooks";
import InputComboBox from "../../../sharedFormComponents/InputComboBox";
import {formatDateUS, validUrl} from "../../../../../helpers/utils";
import useFetchableData from "../../../../../hooks/useFetchableData";
import RectangularSkeleton from "../../../shared/Skeleton/RectangularSkeleton";
import DeleteIcon from "@material-ui/icons/Delete";
import SortableTable from "../../../shared/Tables/SortableTable";
import FormDialog from "../../../shared/Dialogs/FormDialog";
import CustomSnackbar from "../../../../user/shared/CustomSnackbar";
import useSnackbar from "../../../../../hooks/useSnackbar";
import useInvestorUpdater from "../../hooks/useInvestorUpdater";
import useInvestorDeleter from "../../hooks/useInvestorDeleter";
import UrlTextField from "../../../shared/Inputs/UrlTextField";
import PieceOfNewsFo from "../../../news/PieceOfNewsFo";

const NewsSection = (
    {
        isEditing, handleOnChange, handleOnChangeInField,
        handleOnError, session, newsTypes, newsCategories,
        fetchNews, investor, ...props
    }
) => {
    const [pieceOfNewsErrors, setPieceOfNewsErrors] = useState([]);
    const [selectedNews, setSelectedNews] = useState(null);
    const [openNewPieceOfNewsFo, setOpenNewPieceOfNewsFo] = useState(false);
    const [news, setNews] = useState([]);
    const {data: newsData, initialFetch, fetchData} = useFetchableData(fetchNews, "news");
    const snackbar = useSnackbar();
    const updater = useInvestorUpdater(session, snackbar);
    const deleter = useInvestorDeleter(session, snackbar);

    useEffect(() => {
        handleOnChangeInField("news_attributes", [...news], initialFetch)
    }, [news])

    useEffect(() => {
        setNews(newsData)
        setPieceOfNewsErrors(Array(newsData.length).fill(false))
    }, [newsData])

    const setPieceOfNewsError = (index, error) => {
        let newErrors = [...pieceOfNewsErrors];
        newErrors[index] = error;
        setPieceOfNewsErrors(newErrors);
    }

    useEffect(() => {
        handleOnError(pieceOfNewsErrors.some((error) => error))
    }, [pieceOfNewsErrors]);

    const handleAddPieceOfNews = () => {
        let newNews = [...news];

        newNews.push({
            title: '',
            source_url: '',
            news_type: '',
            funded_company_id: undefined,
            news_category_ids: undefined,
            firm_ids: [],
            firm_type: undefined,
            item_contact_ids: [],
            item_contact_type: undefined,
            publisher_id: undefined,
            article_content: '',
            written_at: null,
            medium: '',
            associated_items: {},
            key: Date.now(),
            id: undefined,
        })

        setNews(newNews);
        setPieceOfNewsErrors(prevState => ([...prevState, true]))
    }

    const handleRemovePieceOfNews = async (event, pieceOfNews) => {
        event.stopPropagation();
        if (window.confirm(`Are you sure you want to delete this news?`)) {
            await deleter.news(pieceOfNews);
            const newNews = news.filter((n) => n.id !== pieceOfNews.id);
            const deletedIndex = news.findIndex((n) => n.id === pieceOfNews.id);
            const newPieceOfNewsErrors = pieceOfNewsErrors.filter((error, index) => index !== deletedIndex);
            setNews(newNews);
            setPieceOfNewsErrors(newPieceOfNewsErrors)
            handleOnError(false);
            setSelectedNews(null);
        }
    }

    const tableNews = news.filter((pieceOfNews) => !pieceOfNews._destroy && !pieceOfNews.key);
    const removeError = (index) => setPieceOfNewsErrors(pieceOfNewsErrors.filter((error, i) => index !== i));

    const afterCreatePieceOfNews = () => {
        setOpenNewPieceOfNewsFo(false)
        fetchData()
    }

    return (
        <Grid container item xs={12}>
            <RectangularSkeleton loading={!initialFetch} width={1000} height={300}>
                <Paper className="form-container" elevation={3}>
                    <Grid item xs={12}>
                        <h3>News</h3>
                    </Grid>
                    <Grid container spacing={3}>
                        <Grid item xs={12}>
                            {
                                tableNews.length > 0 && <SortableTable
                                    headCells={[
                                        { id: 'title', numeric: false, disablePadding: false, label: 'Title', sortable: true },
                                        { id: 'written_at', numeric: false, disablePadding: false, label: 'Written At', sortable: true },
                                        { id: 'news_type', numeric: false, disablePadding: false, label: 'News Type', sortable: true },
                                        { id: 'sources', numeric: false, disablePadding: false, label: 'Sources', sortable: true },
                                        { id: 'options', numeric: false, disablePadding: false, label: null },
                                    ]}
                                    rows={tableNews}
                                    RowRenderer={
                                        (row, _isSelected, index, _handleCheck) => (<TableRow
                                            hover
                                            tabIndex={-1}
                                            key={row.id}
                                            style={{cursor: "pointer"}}
                                            onClick={(event) => setSelectedNews(row.id)}
                                        >
                                            <TableCell>
                                                <Link
                                                    onClick={(event) => event.stopPropagation()}
                                                    href={row.source_url}
                                                    target={"_blank"}
                                                >
                                                    {row.title}
                                                </Link>
                                            </TableCell>
                                            <TableCell>
                                                {formatDateUS(row.written_at)}
                                            </TableCell>
                                            <TableCell>
                                                {row.news_type}
                                            </TableCell>
                                            <TableCell>
                                                {Array.isArray(row.sources) ? row.sources.join(', ') : (row.source || '')}
                                            </TableCell>
                                            <TableCell>
                                                <Link onClick={(event) => handleRemovePieceOfNews(event, row)}>
                                                    Delete
                                                </Link>
                                            </TableCell>
                                        </TableRow>)
                                    }
                                    initialOrderBy={"title"}
                                />
                            }
                        </Grid>
                        <Grid item xs={12}>
                            <Button variant="contained" color="primary" onClick={() => setOpenNewPieceOfNewsFo(true)}>Add New Piece of News</Button>
                        </Grid>
                        <Grid item xs={12}>
                            <React.Fragment>
                                    <PieceOfNewsFo
                                        adminUserSession={session}
                                        pieceOfNewsId={selectedNews}
                                        isDraft={props.isDraft}
                                        onClose={() => setSelectedNews(null)}
                                        open={!!selectedNews}
                                        newsTypes={newsTypes}
                                        onDataUpdate={(data, index) => {
                                            let copy = [...news];

                                            copy[index] = data;
                                            setNews(copy)
                                            setSelectedNews(null)
                                        }
                                        }
                                        itemIndex={news.findIndex(x => x.id === selectedNews)}
                                        initialInvestor={{id: investor.id, name: investor.name}}
                                    />

                                        <CustomSnackbar
                                            open={snackbar.open}
                                            closeHandler={snackbar.close}
                                            message={snackbar.message}
                                            options={{
                                                severity: 'success'
                                            }}
                                        />
                            </React.Fragment>
                        </Grid>
                    </Grid>
                </Paper>
            </RectangularSkeleton>
            {
                openNewPieceOfNewsFo ?
                    <PieceOfNewsFo
                        adminUserSession={session}
                        pieceOfNewsId={undefined}
                        isDraft={props.isDraft}
                        onClose={() => setOpenNewPieceOfNewsFo(false)}
                        open={openNewPieceOfNewsFo}
                        newsTypes={newsTypes}
                        afterCreatePieceOfNews={afterCreatePieceOfNews}
                        itemIndex={undefined}
                        initialInvestor={{id: investor.id, name: investor.name}}
                    />
                    : null
            }
        </Grid>
    )
}

const PieceOfNews = ({
                         session, pieceOfNewsData, index,
                         news, setNews, newsTypes, newsCategories, removeNewsError,
                         handleOnError, isEditing, isNew, deleter,
}) => {
    const [pieceOfNews, setPieceOfNews] = useState({ ...pieceOfNewsData });
    const [selectedPublisher, setSelectedPublisher] = useState({});
    const [selectedFundedCompany, setSelectedFundedCompany] = useState({});
    const [selectedNewsCategories, setSelectedNewsCategories] = useState(pieceOfNewsData?.news_category_ids || []);
    const [publisherError, setPublisherError] = useState(!isEditing);
    const [fundedCompanyError, setFundedCompanyError] = useState(!isEditing);
    const fetchers = useDataFetchers(session);

    const {reset: resetTitle, validate: validateTitle, ...title} = useField(
        "text",
        true,
        pieceOfNews.title || ""

    );
    const {reset: resetSourceUrl, validate: validateSourceUrl, ...source_url} = useFieldWithValidation(
        "text",
        pieceOfNews.source_url || "",
        (value) => (validUrl(value)),
        true
    );
    const {reset: resetBiography, ...article_content} = useField(
        'text',
        true,
        pieceOfNews.article_content || "",
    );

    const requiredErrors = () => {
        return title.error || source_url.error || article_content.error || _.isEmpty(pieceOfNews.news_type)
            || publisherError || (pieceOfNews.news_type === 'Investment Activity' && fundedCompanyError)
            || _.isEmpty(pieceOfNews.medium)
    }

    useEffect(() => {
        handleOnError(requiredErrors());
    }, [
        title.error, source_url.error, article_content.error,
        pieceOfNews.news_type, publisherError, fundedCompanyError,
        pieceOfNews.medium
    ]);

    const [errorMessage, setErrorMessage] = useState({});

    const [openPublisherForm, setOpenPublisherForm] = useState(false);

    useEffect(() => {
        if (pieceOfNewsData?.id !== undefined && pieceOfNewsData?.id !== null) {
            setSelectedFundedCompany(pieceOfNewsData?.extra_data?.funded_company || {});
            setSelectedPublisher(pieceOfNewsData?.extra_data?.publisher || {});
        }
    }, [pieceOfNewsData.id])

    useEffect(() => {
        if (pieceOfNews._destroy) {
            const newNews = news.filter((pieceOfNews, i) => index !== i);
            setNews(newNews)
            removeNewsError(index)
        } else {
            let newNews = [...news]
            newNews[index] = pieceOfNews;
            setNews(newNews);
        }
    }, [pieceOfNews])

    useEffect(() => {
        setPieceOfNews(prevState => ({
            ...prevState,
            news_category_ids: selectedNewsCategories,
        }));
    }, [selectedNewsCategories])

    const handleOnChange = (event) => {
        event.persist();

        setPieceOfNews(prevState => ({
            ...prevState,
            [event.target.name]: event.target.value,
        }));
    }

    const handleOnChangeAutocomplete = (field, selectedValue) => {
        let fieldData = {};

        let associatedItems = { ...pieceOfNews.associated_items };

        switch (field) {
            case 'publisher_id':
                fieldData = {
                    publisher_id: selectedValue?.id,
                    publisher_name: selectedValue?.name,
                }

                setSelectedPublisher(selectedValue);
                break;
            case 'funded_company_id':
                fieldData = {
                    funded_company_id: selectedValue?.id,
                }

                if (selectedValue !== undefined) {
                    fieldData = {
                        ...fieldData,
                        associated_items: {
                            ...associatedItems,
                            FundedCompany: [{ id: selectedValue.id, name: selectedValue.name, type: 'FundedCompany' }]
                        }
                    }
                } else {
                    fieldData = {
                        ...fieldData,
                        associated_items: {
                            ...associatedItems,
                        }
                    }
                }

                setSelectedFundedCompany(selectedValue);
                break;
        }

        setPieceOfNews(prevState => ({
            ...prevState,
            ...fieldData,
        }));
    }

    const handleDateChange = (date) => {
        setPieceOfNews(prevState => ({
            ...prevState,
            written_at: date,
        }));
    }

    const handleSelectOne = (id) => {
        const selectedIndex = selectedNewsCategories.indexOf(id);
        let newSelectedNewsCategories = [];

        if (selectedIndex === -1) {
            newSelectedNewsCategories = newSelectedNewsCategories.concat(selectedNewsCategories, id);
        } else if (selectedIndex === 0) {
            newSelectedNewsCategories = newSelectedNewsCategories.concat(
                selectedNewsCategories.slice(1)
            );
        } else if (selectedIndex === selectedNewsCategories.length - 1) {
            newSelectedNewsCategories = newSelectedNewsCategories.concat(
                selectedNewsCategories.slice(0, -1)
            );
        } else if (selectedIndex > 0) {
            newSelectedNewsCategories = newSelectedNewsCategories.concat(
                selectedNewsCategories.slice(0, selectedIndex),
                selectedNewsCategories.slice(selectedIndex + 1)
            );
        }

        setSelectedNewsCategories(newSelectedNewsCategories);
    }

    const handleOpenNewPublisherModal = () => {
        setOpenPublisherForm(true);
    }

    const handleCloseNewPublisherModal = () => {
        setOpenPublisherForm(false);
    }

    const afterCreatePublisher = (publisher) => {
        setSelectedPublisher(publisher);

        setPieceOfNews(prevState => ({
            ...prevState,
            publisher_id: publisher?.id,
        }));
    }

    const handleRemovePieceOfNews = async () => {
        if (window.confirm('Are you sure you want to delete this piece of news?')) {
            if (pieceOfNews.id) {
                await deleter.news(pieceOfNews);
            }

            setPieceOfNews(prevState => ({
                ...prevState,
                _destroy: true,
            }));
        }
    }

    const RenderNewsContainer = () => {
        return <Grid container spacing={3} className='admin-piece-of-news-fields'>
            <Grid item xs={11}>
                { isNew && "News Form"}
            </Grid>
            <Grid item xs={1}>
                <Button onClick={handleRemovePieceOfNews} className="error">
                    <DeleteIcon />
                </Button>
            </Grid>
            <Grid item xs={3}>
                <FormControl fullWidth>
                    <TextField
                        variant="outlined"
                        {...title}
                        onBlur={handleOnChange}
                        label={"Title"}
                        name={"title"}
                        maxLength={40}
                    />
                    <span>{errorMessage['title']}</span>
                </FormControl>
            </Grid>

            <Grid item xs={3}>
                <FormControl fullWidth>
                    <UrlTextField
                        variant="outlined"
                        {...source_url}
                        onBlur={handleOnChange}
                        label={"Source URL"}
                        name={"source_url"}
                        maxLength={150}
                    />
                    <span>{errorMessage['source_url']}</span>
                </FormControl>
            </Grid>

            <Grid item xs={3}>
                <InputComboBox
                    value={pieceOfNews.news_type || ''}
                    label={'News Type'}
                    field={'news_type'}
                    name={'news_type'}
                    handleOnChange={handleOnChange}
                    options={newsTypes.map((newsType) => ({id: newsType, name: newsType}))}
                    menuClass={'input-combo-menu'}
                    error={_.isEmpty(pieceOfNews.news_type)}
                    required
                />
            </Grid>

            <Grid item xs={3} style={{visibility: pieceOfNews.news_type === 'Investment Activity' ? 'visible' : 'hidden'}}>
                <SingleAsyncAutoComplete
                    fetch={fetchers.fundedCompanies}
                    handleOnChange={(value) => handleOnChangeAutocomplete('funded_company_id', value)}
                    handleOnError={(error) => setFundedCompanyError(error)}
                    value={selectedFundedCompany}
                    label={'Funded Company'}
                    field={'funded_company_id'}
                    required
                />
                <span>{errorMessage['funded_company_id']}</span>
            </Grid>

            <Grid item xs={3}>
                <SingleAsyncAutoComplete
                    fetch={fetchers.publishers}
                    handleOnChange={(value) => handleOnChangeAutocomplete('publisher_id', value)}
                    handleOnError={(error) => setPublisherError(error)}
                    value={selectedPublisher}
                    label={'Publisher'}
                    field={'publisher_id'}
                    required
                />
                <Link onClick={handleOpenNewPublisherModal}>New Publisher</Link>
                <span>{errorMessage['publisher_id']}</span>
            </Grid>

            <Grid item xs={9}>
                <FormControl fullWidth>
                    <FormGroup row>
                        {
                            newsCategories.map((category) => (
                                <FormControlLabel
                                    key={category.id}
                                    control={
                                        <Checkbox
                                            checked={
                                                selectedNewsCategories.includes(category.id)
                                            }
                                            name="news_categories_ids"
                                            color="primary"
                                            onChange={(event) => handleSelectOne(category.id)}
                                            value={category.id}
                                        />
                                    }
                                    label={category.name}
                                />
                            ))
                        }
                    </FormGroup>
                </FormControl>
            </Grid>

            <Grid item xs={3}>
                <FormControl fullWidth>
                    <MuiPickersUtilsProvider utils={DateFnsUtils}>
                        <KeyboardDatePicker
                            disableFuture
                            inputVariant="outlined"
                            label="Written At"
                            format="MM/dd/yyyy"
                            value={pieceOfNews.written_at}
                            onChange={handleDateChange}
                            KeyboardButtonProps={{
                                'aria-label': 'change date',
                            }}
                        />
                    </MuiPickersUtilsProvider>
                    <span>{errorMessage['written_at']}</span>
                </FormControl>
            </Grid>

            <Grid item xs={4}>
                <FormControl component="fieldset" error={_.isEmpty(pieceOfNews.medium)} fullWidth>
                    <FormLabel component="legend">News Medium</FormLabel>
                    <RadioGroup row aria-label="medium" name="medium" value={pieceOfNews.medium} onChange={handleOnChange}>
                        <FormControlLabel value="written" control={<Radio required />} label="Written" />
                        <FormControlLabel value="video" control={<Radio required />} label="Video" />
                    </RadioGroup>
                </FormControl>
            </Grid>

            <Grid item xs={12}>
                <TextField
                    {...article_content}
                    fullWidth
                    label="Article Content"
                    variant="outlined"
                    name={'article_content'}
                    multiline={true}
                    maxRows={10}
                    minRows={10}
                    onBlur={handleOnChange}
                />
                <span>{errorMessage['article_content']}</span>
            </Grid>

            {
                openPublisherForm ?
                    <PublisherForm
                        session={session}
                        onClose={handleCloseNewPublisherModal}
                        open={openPublisherForm}
                        afterCreatePublisher={afterCreatePublisher}
                    />
                    : null
            }
        </Grid>
    }

    return (
        pieceOfNews._destroy === true ? null :
            <>
                {
                    isNew ? <Paper elevation={3} className={"paper-sub-form"}>
                        {RenderNewsContainer()}
                    </Paper> : RenderNewsContainer()
                }
            </>
    )
}

export default NewsSection;